import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TRIAL_OFFER_EVENTS, TRIAL_OFFER_LINK_LOCATIONS } from 'src/app/constants/trial-offer.constants';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { BillingApiData, BillingDetailsApiData, GenericApiResponse } from 'src/app/pages/new-billing/types/api.types';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { extractBillingCycleFromPlanId } from 'src/app/pages/new-billing/utils/plan-maps';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { BillingApiService } from 'src/app/services/billing/billing-api.service';
import { BillingService } from 'src/app/services/billing/billing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import {
  TRIAL_OFFER_ERROR_CODES,
  TrialOfferApiData,
  TrialOfferErrorCode,
  TrialOfferProgress,
  TrialOfferState
} from './trial-offer.types';

type TrialOfferApiResponse = GenericApiResponse<TrialOfferApiData>;

const DAY_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class TrialOfferService {
  constructor(
    private readonly billingApi: BillingApiService,
    private readonly billingService: BillingService,
    private readonly segment: SegmentService,
    private readonly amplitude: AmplitudeService,
    private readonly store: Store,
    private readonly translate: TranslateService,
  ) {}

  getState(billingDetails: BillingDetailsApiData | null | undefined): TrialOfferState {
    if (!this.store.selectSnapshot(AuthState.hasBillingAccess)) {
      return 'no-billing-access';
    }

    if (!billingDetails?.trialOffer) {
      return 'offer';
    }

    const progress = this.getProgress(billingDetails);
    return progress && !progress.canceled ? 'active' : 'availed';
  }

  getProgress(billingDetails: BillingDetailsApiData | null | undefined): TrialOfferProgress | null {
    const offer = billingDetails?.trialOffer;
    if (!offer || offer.upgradedAt) {
      return null;
    }

    const remainingMs = new Date(offer.trialExpiry).getTime() - Date.now();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
      return null;
    }

    return {
      targetPlanId: offer.targetPlanId,
      trialExpiry: offer.trialExpiry,
      daysLeft: Math.ceil(remainingMs / DAY_MS),
      canceled: !!offer.canceledAt,
    };
  }

  isAutoUpgradePending(billingDetails: BillingDetailsApiData | null | undefined): boolean {
    const progress = this.getProgress(billingDetails);
    return !!progress && !progress.canceled;
  }

  isOfferPlan(
    billingDetails: BillingDetailsApiData | null | undefined,
    plan: ConcretePlan | null | undefined,
    payRecurrence: PricePeriodicity,
  ): boolean {
    const targetPlanId = this.getProgress(billingDetails)?.targetPlanId;
    if (!targetPlanId || !plan) {
      return false;
    }

    const targetRecurrence = extractBillingCycleFromPlanId(targetPlanId) === 'yearly' ? 'year' : 'month';
    return plan.productId === targetPlanId.split('_')[0] && payRecurrence === targetRecurrence;
  }

  // The Paddle subscription stays on the paid plan for the whole trial, so plan changes must be
  // computed against it rather than against the trial plan billing-details reports as current.
  paidPlanBillingData(billingData: BillingApiData): BillingApiData {
    const { billingDetails } = billingData;
    if (!this.getProgress(billingDetails) || !billingDetails.actualPlanId) {
      return billingData;
    }

    return {
      ...billingData,
      billingDetails: {
        ...billingDetails,
        currentPlanId: billingDetails.actualPlanId,
        currentPlan: billingDetails.actualPlan,
      },
    };
  }

  getPaidPlanName(billingDetails: BillingDetailsApiData | null | undefined): string | null {
    const tier = billingDetails?.actualPlanId?.split('_')[0];
    return tier ? this.translate.instant(`billing.${tier}`) : null;
  }

  activate(offerKey: string): Observable<TrialOfferApiData> {
    return this.billingApi
      .post<TrialOfferApiResponse>('customer/trial-offer', { offerKey })
      .pipe(
        map(response => response.data),
        tap(offer => {
          this.track(TRIAL_OFFER_EVENTS.activated, {
            offerkey: offerKey,
            activatedat: offer.activatedAt,
            trialexpiry: offer.trialExpiry,
            targetplanid: offer.targetPlanId,
            linklocation: TRIAL_OFFER_LINK_LOCATIONS.offerDialog,
          });
          this.refreshBillingDetails();
        }),
        catchError((error: HttpErrorResponse) => {
          this.track(TRIAL_OFFER_EVENTS.activationFailed, {
            offerkey: offerKey,
            errorcode: this.readErrorCode(error) ?? 'unknown',
            linklocation: TRIAL_OFFER_LINK_LOCATIONS.offerDialog,
          });
          return throwError(() => error);
        }),
      );
  }

  cancel(): Observable<TrialOfferApiData> {
    return this.billingApi
      .delete<TrialOfferApiResponse>('customer/trial-offer')
      .pipe(
        map(response => response.data),
        tap(offer => {
          this.track(TRIAL_OFFER_EVENTS.cancelled, {
            offerkey: offer.offerKey,
            canceledat: offer.canceledAt,
            trialexpiry: offer.trialExpiry,
            linklocation: TRIAL_OFFER_LINK_LOCATIONS.cancelDialog,
          });
          this.refreshBillingDetails();
        }),
        catchError((error: HttpErrorResponse) => {
          this.track(TRIAL_OFFER_EVENTS.cancelFailed, {
            errorcode: this.readErrorCode(error) ?? 'unknown',
            linklocation: TRIAL_OFFER_LINK_LOCATIONS.cancelDialog,
          });
          return throwError(() => error);
        }),
      );
  }

  // The global HTTP interceptor rethrows the parsed billing error body, not the HttpErrorResponse.
  readErrorCode(error: HttpErrorResponse | { error?: unknown }): TrialOfferErrorCode | null {
    const body = error instanceof HttpErrorResponse ? error.error : error;
    const code = body?.error;
    return TRIAL_OFFER_ERROR_CODES.includes(code) ? code : null;
  }

  track(event: string, properties: Record<string, unknown>): void {
    this.segment.trackInAppEvents(event, properties);
    this.amplitude.trackEvent(event, properties);
  }

  // 'no-cache' is required: with the default the store replays the pre-activation snapshot
  // and trialOffer never appears.
  private refreshBillingDetails(): void {
    this.billingService.getBillingDetails('no-cache').subscribe({
      error: () => undefined,
    });
  }
}
